/**
 * SupplierModule.jsx
 * Tedarikçi (Toptancı) Cari Hesapları + Akıllı Föy İşleme + Ödeme Girişi + Ekstre PDF
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import ZenPagination from "./ZenPagination";
import {
  Building2, Plus, Trash2, Pencil, X, Upload, Sparkles, FileText,
  ChevronRight, Search, Phone, Mail, MapPin, Receipt, Loader2,
  Check, AlertCircle, Image as ImageIcon, TrendingDown, TrendingUp,
  DollarSign, ArrowUpCircle, Download,
} from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { fmtDate } from "../appUtils";

// ─── Yardımcılar ─────────────────────────────────────────────────────────────
const fmtBal = (v, cur = "TL") => {
  const abs = Math.abs(v).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${abs} ${cur}`;
};

function resizeImageToBase64(file, maxPx = 1600, quality = 0.9) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// ─── Ana Modül ───────────────────────────────────────────────────────────────
export default function SupplierModule() {
  const [suppliers, setSuppliers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);
  const [showFoyModal, setShowFoyModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);

  const load = useCallback(async () => {
    const [s, st] = await Promise.all([
      api.get("/suppliers"),
      api.get("/supplier-foyer/stats"),
    ]);
    setSuppliers(s.data);
    setStats(st.data);
  }, []);

  const [ledger, setLedger] = useState(null); // birleşik ekstre (föy + ödemeler)
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerPageSize, setLedgerPageSize] = useState(25);

  useEffect(() => { load(); }, [load]);

  const loadLedger = useCallback(async (supplierId) => {
    const { data } = await api.get(`/supplier-ledger/${supplierId}`);
    setLedger(data);
    setLedgerPage(1);
  }, []);

  useEffect(() => {
    if (selected) loadLedger(selected.id);
    else setLedger(null);
  }, [selected, loadLedger]);

  const refreshSelected = useCallback(async () => {
    load();
    if (selected) loadLedger(selected.id);
  }, [load, loadLedger, selected]);

  const deleteSupplier = async (id) => {
    if (!window.confirm("Bu tedarikçiyi silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/suppliers/${id}`);
      toast.success("Tedarikçi silindi");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silme hatası");
    }
  };

  const deleteFoy = async (fid) => {
    if (!window.confirm("Bu föy kaydını silmek istiyor musunuz? Borç geri alınacak.")) return;
    await api.delete(`/supplier-foyer/${fid}`);
    toast.success("Föy silindi, borç güncellendi");
    const { data } = await api.get("/suppliers");
    setSuppliers(data);
    const upd = data.find((s) => s.id === selected?.id);
    if (upd) setSelected(upd);
    if (selected) loadLedger(selected.id);
    load();
  };

  const deletePayment = async (pid) => {
    if (!window.confirm("Bu ödeme kaydını silmek istiyor musunuz? Bakiye geri alınacak.")) return;
    await api.delete(`/supplier-payments/${pid}`);
    toast.success("Ödeme silindi");
    const { data } = await api.get("/suppliers");
    setSuppliers(data);
    const upd = data.find((s) => s.id === selected?.id);
    if (upd) setSelected(upd);
    if (selected) loadLedger(selected.id);
    load();
  };

  const filtered = suppliers.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4 zen-anim-up">
      {/* ─── İstatistik kartları ─────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Building2} label="Tedarikçi" value={stats.supplierCount || 0} color="blue" hint="Kayıtlı tedarikçi sayısı" />
        <StatCard icon={Receipt} label="Toplam Föy" value={stats.foyCount || 0} color="purple" hint="Kesilen mal kabul föyü sayısı" />
        <StatCard icon={TrendingDown} label="Toplam Borcum" value={`${(stats.totalDebt || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} TL`} color="red" hint="Tedarikçilere olan toplam borcunuz" />
        <StatCard icon={TrendingUp} label="Alacağım" value={`${(stats.totalCredit || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} TL`} color="green" hint="Tedarikçiden alacağınız (fazla ödeme vb.)" />
      </div>

      <div className="flex gap-4">
        {/* ─── Sol: Tedarikçi Listesi ───────────────────────── */}
        <div className="w-72 flex-shrink-0 space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
              <input
                className="zen-input !pl-7 !py-1.5 !text-xs w-full"
                placeholder="Tedarikçi ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="supplier-search"
              />
            </div>
            <button
              onClick={() => { setEditSupplier(null); setShowForm(true); }}
              className="zen-btn-primary !px-2 !py-1.5 flex-shrink-0"
              data-testid="supplier-add-btn"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-1 max-h-[calc(100vh-300px)] overflow-y-auto pr-1">
            {filtered.length === 0 && (
              <div className="text-xs text-slate-500 text-center py-8">
                {search ? "Sonuç bulunamadı" : "Henüz tedarikçi yok"}
              </div>
            )}
            {filtered.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelected(s)}
                data-testid={`supplier-item-${s.id}`}
                className={`w-full text-left p-3 rounded-lg border transition-all ${
                  selected?.id === s.id
                    ? "border-[#E5322A] bg-[#E5322A]/5"
                    : "border-slate-200 dark:border-[#22304A] hover:border-slate-300 dark:hover:border-[#3A4A66]"
                }`}
              >
                <div className="font-semibold text-sm">{s.name}</div>
                {s.phone && <div className="text-[11px] text-slate-500 mt-0.5">{s.phone}</div>}
                <div className={`text-xs font-bold mt-1 ${s.balance < 0 ? "text-rose-500" : s.balance > 0 ? "text-emerald-500" : "text-slate-400"}`}>
                  {s.balance < 0 ? `Borcum: ${fmtBal(s.balance, s.currency)}` :
                   s.balance > 0 ? `Alacağım: ${fmtBal(s.balance, s.currency)}` : "Bakiye: 0"}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ─── Sağ: Detay / Föy Geçmişi ───────────────────── */}
        <div className="flex-1 min-w-0">
          {!selected ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <Building2 className="w-10 h-10 mb-3 opacity-30" />
              <p className="text-sm">Soldaki listeden bir tedarikçi seçin</p>
            </div>
          ) : (
            <SupplierDetail
              supplier={selected}
              ledger={ledger}
              onEdit={() => { setEditSupplier(selected); setShowForm(true); }}
              onDelete={() => deleteSupplier(selected.id)}
              onDeleteFoy={deleteFoy}
              onDeletePayment={deletePayment}
              onNewFoy={() => setShowFoyModal(true)}
              onNewPayment={() => setShowPayModal(true)}
              onRefresh={refreshSelected}
            />
          )}
        </div>
      </div>

      {/* ─── Modal: Tedarikçi Formu ───────────────────────────── */}
      {showForm && (
        <SupplierFormModal
          initial={editSupplier}
          onClose={() => setShowForm(false)}
          onSaved={(saved) => {
            load();
            setShowForm(false);
            setSelected(saved);
          }}
        />
      )}

      {/* ─── Modal: Akıllı Föy İşleme ────────────────────────── */}
      {showFoyModal && selected && (
        <FoyProcessModal
          supplier={selected}
          onClose={() => setShowFoyModal(false)}
          onSaved={() => {
            setShowFoyModal(false);
            const refresh = async () => {
              const { data } = await api.get("/suppliers");
              setSuppliers(data);
              const upd = data.find((s) => s.id === selected.id);
              if (upd) setSelected(upd);
              loadLedger(selected.id);
              load();
            };
            refresh();
          }}
        />
      )}

      {/* ─── Modal: Manuel Ödeme ─────────────────────────────── */}
      {showPayModal && selected && (
        <PaymentModal
          supplier={selected}
          onClose={() => setShowPayModal(false)}
          onSaved={() => {
            setShowPayModal(false);
            const refresh = async () => {
              const { data } = await api.get("/suppliers");
              setSuppliers(data);
              const upd = data.find((s) => s.id === selected.id);
              if (upd) setSelected(upd);
              loadLedger(selected.id);
              load();
            };
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ─── Stat Kartı ──────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color, hint }) {
  const colors = {
    blue:   "text-blue-600   bg-blue-50   dark:bg-blue-900/20",
    purple: "text-purple-600 bg-purple-50 dark:bg-purple-900/20",
    red:    "text-rose-600   bg-rose-50   dark:bg-rose-900/20",
    green:  "text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20",
  };
  return (
    <div className="zen-panel p-4 flex items-center gap-3">
      <div className={`p-2 rounded-lg ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-lg font-extrabold zen-mono">{value}</div>
        {hint && <div className="text-[9px] text-slate-400 mt-0.5 leading-tight">{hint}</div>}
      </div>
    </div>
  );
}

// ─── Tedarikçi Detay ─────────────────────────────────────────────────────────
function SupplierDetail({ supplier, ledger, onEdit, onDelete, onDeleteFoy, onDeletePayment, onNewFoy, onNewPayment, onRefresh }) {
  const [showImgId, setShowImgId] = useState(null);

  const rows = ledger?.rows || [];
  const pagedRows = rows.slice((ledgerPage - 1) * ledgerPageSize, ledgerPage * ledgerPageSize);
  const cur = supplier.currency || "TL";

  const downloadPdf = async () => {
    try {
      const { buildSupplierStatementPdf } = await import("../utils/pdfUtils");
      await buildSupplierStatementPdf(supplier, rows);
    } catch (e) {
      toast.error("PDF oluşturulamadı: " + e.message);
    }
  };

  return (
    <div className="space-y-4">
      {/* ─── Başlık ──────────────────────────────────────────── */}
      <div className="zen-panel p-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-extrabold">{supplier.name}</h3>
            <div className="flex flex-wrap gap-3 mt-1 text-sm text-slate-500">
              {supplier.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{supplier.phone}</span>}
              {supplier.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{supplier.email}</span>}
              {supplier.address && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{supplier.address}</span>}
              {supplier.taxNo && <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" />VKN: {supplier.taxNo}</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={onEdit} className="zen-btn !px-2 !py-1.5" data-testid="supplier-edit-btn"><Pencil className="w-4 h-4" /></button>
            <button onClick={onDelete} className="zen-btn !px-2 !py-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20" data-testid="supplier-delete-btn"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Bakiye kartı */}
        <div className={`mt-3 p-3 rounded-lg flex items-center gap-3 ${supplier.balance < 0 ? "bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800" : supplier.balance > 0 ? "bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800" : "bg-slate-50 dark:bg-[#1A2235] border border-slate-200 dark:border-[#22304A]"}`}>
          <DollarSign className={`w-6 h-6 ${supplier.balance < 0 ? "text-rose-500" : supplier.balance > 0 ? "text-emerald-500" : "text-slate-400"}`} />
          <div className="flex-1">
            <div className="text-xs font-semibold text-slate-500">
              {supplier.balance < 0 ? "Benim Borcum" : supplier.balance > 0 ? "Tedarikçide Alacağım" : "Bakiye Sıfır"}
            </div>
            <div className={`text-2xl font-extrabold zen-mono ${supplier.balance < 0 ? "text-rose-500" : supplier.balance > 0 ? "text-emerald-500" : "text-slate-400"}`}>
              {fmtBal(supplier.balance, cur)}
            </div>
          </div>
        </div>

        {/* Aksiyon butonları */}
        <div className="mt-3 grid grid-cols-3 gap-2">
          <button
            onClick={onNewFoy}
            className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold text-sm hover:opacity-90 transition col-span-1"
            data-testid="foy-process-btn"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Föy İşle (AI)
          </button>
          <button
            onClick={onNewPayment}
            className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold text-sm hover:opacity-90 transition col-span-1"
            data-testid="payment-add-btn"
          >
            <ArrowUpCircle className="w-3.5 h-3.5" />
            Ödeme Yap
          </button>
          <button
            onClick={downloadPdf}
            className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-slate-300 dark:border-[#22304A] text-slate-600 dark:text-slate-300 font-semibold text-sm hover:bg-slate-50 dark:hover:bg-[#1A2235] transition col-span-1"
            data-testid="ekstre-pdf-btn"
          >
            <Download className="w-3.5 h-3.5" />
            Ekstre PDF
          </button>
        </div>
      </div>

      {/* ─── Birleşik Ekstre (Föy + Ödemeler) ───────────────── */}
      <div className="zen-panel overflow-hidden">
        <div className="p-4 border-b border-slate-100 dark:border-[#22304A] font-semibold flex items-center gap-2">
          <Receipt className="w-4 h-4 text-slate-400" />
          Cari Ekstre ({rows.length} hareket)
        </div>

        {rows.length === 0 && (
          <div className="text-sm text-slate-400 text-center py-8">Henüz kayıt yok</div>
        )}

        {/* Ekstre tablosu */}
        {rows.length > 0 && (
          <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 dark:border-[#22304A] text-xs text-slate-500 uppercase">
                  <th className="px-4 py-2 text-left">Tarih</th>
                  <th className="px-4 py-2 text-left">Açıklama</th>
                  <th className="px-4 py-2 text-right text-rose-500">Borç</th>
                  <th className="px-4 py-2 text-right text-emerald-500">Ödeme</th>
                  <th className="px-4 py-2 text-right">Bakiye</th>
                  <th className="px-4 py-2 w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-[#22304A]">
                {pagedRows.map((r) => {
                  const isFoy = r.type === "borc";
                  return (
                    <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-[#0F172A]/50 transition-colors" data-testid={`ledger-row-${r.id}`}>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.date)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${isFoy ? "bg-rose-400" : "bg-emerald-400"}`} />
                          <div>
                            <div className="text-xs font-medium leading-tight">{r.description}</div>
                            {r.note && <div className="text-[11px] text-slate-400 mt-0.5">{r.note}</div>}
                            {/* Föy görseli butonu */}
                            {isFoy && (() => {
                              const fId = r.id;
                              return null; // görsel erişimi için ayrı endpoint gerekir
                            })()}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right zen-mono font-semibold text-rose-500 whitespace-nowrap">
                        {isFoy ? `${r.borc.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ${cur}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-right zen-mono font-semibold text-emerald-500 whitespace-nowrap">
                        {!isFoy ? `${r.odeme.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ${cur}` : "—"}
                      </td>
                      <td className={`px-4 py-3 text-right zen-mono font-extrabold whitespace-nowrap ${r.bakiye < 0 ? "text-rose-500" : r.bakiye > 0 ? "text-emerald-500" : "text-slate-400"}`}>
                        {Math.abs(r.bakiye).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {cur}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => isFoy ? onDeleteFoy(r.id) : onDeletePayment(r.id)}
                          className="p-1 rounded hover:bg-rose-50 dark:hover:bg-rose-900/20 text-rose-300 hover:text-rose-500 transition"
                          data-testid={`ledger-delete-${r.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              {/* Toplam satırı */}
              <tfoot>
                <tr className="border-t-2 border-slate-200 dark:border-[#22304A] bg-slate-50 dark:bg-[#0F172A]">
                  <td colSpan={2} className="px-4 py-2 text-xs font-bold text-slate-500 uppercase">Toplam</td>
                  <td className="px-4 py-2 text-right zen-mono font-extrabold text-rose-500 text-sm">
                    {rows.reduce((s, r) => s + r.borc, 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {cur}
                  </td>
                  <td className="px-4 py-2 text-right zen-mono font-extrabold text-emerald-500 text-sm">
                    {rows.reduce((s, r) => s + r.odeme, 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {cur}
                  </td>
                  <td className={`px-4 py-2 text-right zen-mono font-extrabold text-base ${supplier.balance < 0 ? "text-rose-500" : "text-emerald-500"}`}>
                    {Math.abs(supplier.balance).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {cur}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
          <ZenPagination total={rows.length} page={ledgerPage} pageSize={ledgerPageSize} onPage={setLedgerPage} onPageSize={(s) => { setLedgerPageSize(s); setLedgerPage(1); }} />
          </>
        )}
      </div>
    </div>
  );
}

// ─── Manuel Ödeme Modalı ──────────────────────────────────────────────────────
function PaymentModal({ supplier, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState("nakit");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const amt = parseFloat(String(amount).replace(",", "."));
    if (!amt || amt <= 0) { toast.error("Geçerli bir tutar girin"); return; }
    setSaving(true);
    try {
      await api.post("/supplier-payments", {
        supplierId: supplier.id,
        supplierName: supplier.name,
        amount: amt,
        date,
        method,
        note,
      });
      toast.success(`${amt.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ${supplier.currency || "TL"} ödeme kaydedildi`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kayıt hatası");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm zen-panel p-5 space-y-4" data-testid="payment-modal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <ArrowUpCircle className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-extrabold">Ödeme Yap</h3>
              <p className="text-xs text-slate-500">{supplier.name}</p>
            </div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        {/* Bakiye bilgisi */}
        {supplier.balance < 0 && (
          <div className="px-3 py-2 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-sm">
            <span className="text-slate-500">Mevcut borç: </span>
            <span className="font-extrabold text-rose-500">{fmtBal(supplier.balance, supplier.currency || "TL")}</span>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500">Ödeme Tutarı ({supplier.currency || "TL"}) *</label>
            <input
              type="number"
              className="zen-input mt-1 !text-lg !font-extrabold zen-mono text-emerald-600"
              placeholder="0,00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              autoFocus
              data-testid="payment-amount-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">Tarih</label>
              <input type="date" className="zen-input mt-1" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Ödeme Yöntemi</label>
              <select className="zen-input mt-1" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="payment-method-select">
                <option value="nakit">Nakit</option>
                <option value="havale">Havale / EFT</option>
                <option value="çek">Çek</option>
                <option value="kredi kartı">Kredi Kartı</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Not (opsiyonel)</label>
            <input className="zen-input mt-1" placeholder="Açıklama..." value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="zen-btn">İptal</button>
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold text-sm hover:opacity-90 transition disabled:opacity-50" data-testid="payment-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Ödemeyi Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Tedarikçi Form Modalı ────────────────────────────────────────────────────
function SupplierFormModal({ initial, onClose, onSaved }) {
  const [f, setF] = useState({
    name: "", phone: "", email: "", address: "", taxNo: "", note: "", currency: "TL",
    ...(initial || {}),
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!f.name.trim()) { toast.error("İsim zorunludur"); return; }
    setSaving(true);
    try {
      let res;
      if (initial) {
        res = await api.put(`/suppliers/${initial.id}`, f);
      } else {
        res = await api.post("/suppliers", f);
      }
      toast.success(initial ? "Tedarikçi güncellendi" : "Tedarikçi eklendi");
      onSaved(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kayıt hatası");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md zen-panel p-5 space-y-4" data-testid="supplier-form-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-extrabold text-lg">{initial ? "Tedarikçi Düzenle" : "Yeni Tedarikçi"}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-500">Firma Adı *</label>
            <input data-testid="supplier-name-input" className="zen-input mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Telefon</label>
            <input className="zen-input mt-1" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">E-posta</label>
            <input className="zen-input mt-1" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">VKN / TC</label>
            <input className="zen-input mt-1" value={f.taxNo} onChange={(e) => setF({ ...f, taxNo: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Para Birimi</label>
            <select className="zen-input mt-1" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
              {["TL", "EUR", "USD"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-500">Adres</label>
            <input className="zen-input mt-1" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-500">Not</label>
            <input className="zen-input mt-1" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="zen-btn">İptal</button>
          <button onClick={save} disabled={saving} className="zen-btn-primary" data-testid="supplier-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {initial ? "Güncelle" : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Akıllı Föy İşleme Modalı ────────────────────────────────────────────────
function FoyProcessModal({ supplier, onClose, onSaved }) {
  const fileRef = useRef();
  const [step, setStep] = useState("upload");   // upload | processing | review | saving
  const [preview, setPreview] = useState(null); // { url, base64 }
  const [extracted, setExtracted] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [note, setNote] = useState("");
  const [targetSupplier, setTargetSupplier] = useState(supplier); // FAZ 2: AI eşleşmesiyle değişebilir
  const suppName = targetSupplier.name;
  const [foyNo, setFoyNo] = useState("");
  const [foyDate, setFoyDate] = useState(new Date().toISOString().slice(0, 10));
  const [total, setTotal] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const [vatAmount, setVatAmount] = useState(0);
  const [currency, setCurrency] = useState("TL");

  const handleFile = async (file) => {
    if (!file) return;
    setStep("processing");

    try {
      if (file.type === "application/pdf") {
        // PDF → backend'e yükle
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/supplier-foyer/process-pdf", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        applyExtracted(data);
        if (data.image_base64) setPreview({ url: data.image_base64, base64: data.image_base64 });
      } else {
        // Resim → base64 yap, AI'ya gönder
        const b64 = await resizeImageToBase64(file);
        setPreview({ url: b64, base64: b64 });
        const { data } = await api.post("/supplier-foyer/process", {
          image_base64: b64,
          mime_type: file.type || "image/jpeg",
        });
        applyExtracted(data);
      }
      setStep("review");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI işleme hatası. Tekrar deneyin.");
      setStep("upload");
    }
  };

  const applyExtracted = (data) => {
    setExtracted(data);
    // supplier_name artık AI'dan gelmiyor — pre-selected supplier kullanılır
    setFoyNo(data.foy_no || "");
    setFoyDate(data.date || new Date().toISOString().slice(0, 10));
    setEditItems(data.items || []);
    setSubtotal(data.subtotal || 0);
    setVatAmount(data.vat_amount || 0);
    setTotal(data.total || 0);
    setCurrency(data.currency || "TL");
    // Müşteri adını otomatik nota yaz
    if (data.customer_note) {
      setNote(`Müşteri: ${data.customer_note}`);
    }
  };

  const recalcTotal = (items) => {
    const sub = items.reduce((s, it) => s + (parseFloat(it.total) || 0), 0);
    setSubtotal(sub);
    // KDV'yi orantısal tut
    const vat = extracted?.subtotal > 0
      ? (sub * (extracted.vat_amount / extracted.subtotal))
      : vatAmount;
    setVatAmount(parseFloat(vat.toFixed(2)));
    setTotal(parseFloat((sub + vat).toFixed(2)));
  };

  const updateItem = (idx, field, val) => {
    const next = editItems.map((it, i) => {
      if (i !== idx) return it;
      const updated = { ...it, [field]: val };
      if (field === "qty" || field === "unitPrice") {
        updated.total = parseFloat(((parseFloat(updated.qty) || 0) * (parseFloat(updated.unitPrice) || 0)).toFixed(2));
      }
      return updated;
    });
    setEditItems(next);
    recalcTotal(next);
  };

  const save = async () => {
    setStep("saving");
    try {
      await api.post("/supplier-foyer", {
        supplierId: targetSupplier.id,
        supplierName: targetSupplier.name,
        foyNo,
        date: foyDate,
        items: editItems,
        subtotal,
        vatAmount,
        total,
        currency,
        imageBase64: preview?.base64 || "",
        note,
      });
      toast.success(`Föy kaydedildi. ${total.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ${currency} borç eklendi.`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kayıt hatası");
      setStep("review");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="w-full max-w-2xl zen-panel p-5 space-y-4 my-4" data-testid="foy-modal">
        {/* Başlık */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-extrabold">Akıllı Föy İşleme</h3>
              <p className="text-xs text-slate-500">{supplier.name}</p>
            </div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        {/* Adım: Yükle */}
        {step === "upload" && (
          <UploadStep fileRef={fileRef} onFile={handleFile} />
        )}

        {/* Adım: İşleniyor */}
        {step === "processing" && (
          <div className="flex flex-col items-center gap-4 py-12">
            <div className="relative">
              <div className="w-16 h-16 rounded-full border-4 border-violet-200 border-t-violet-600 animate-spin" />
              <Sparkles className="absolute inset-0 m-auto w-6 h-6 text-violet-600" />
            </div>
            <div className="text-center">
              <p className="font-semibold">Föy AI ile okunuyor...</p>
              <p className="text-xs text-slate-500 mt-1">Gemini Vision föydeki bilgileri çıkarıyor</p>
            </div>
          </div>
        )}

        {/* Adım: İnceleme & Düzenleme */}
        {step === "review" && (
          <ReviewStep
            preview={preview}
            suppName={suppName}
            supplierMatch={extracted?.supplier_match || null}
            onSwitchSupplier={setTargetSupplier}
            targetSupplier={targetSupplier}
            foyNo={foyNo} setFoyNo={setFoyNo}
            foyDate={foyDate} setFoyDate={setFoyDate}
            currency={currency} setCurrency={setCurrency}
            editItems={editItems}
            subtotal={subtotal}
            vatAmount={vatAmount}
            total={total} setTotal={setTotal}
            note={note} setNote={setNote}
            onUpdateItem={updateItem}
            onBack={() => setStep("upload")}
            onSave={save}
          />
        )}

        {/* Adım: Kaydediliyor */}
        {step === "saving" && (
          <div className="flex flex-col items-center gap-3 py-10">
            <Loader2 className="w-10 h-10 animate-spin text-violet-600" />
            <p className="font-semibold">Kaydediliyor...</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Yükleme Adımı ────────────────────────────────────────────────────────────
function UploadStep({ fileRef, onFile }) {
  const [dragging, setDragging] = useState(false);

  return (
    <div>
      <div
        className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer ${dragging ? "border-violet-500 bg-violet-50 dark:bg-violet-900/10" : "border-slate-300 dark:border-[#22304A] hover:border-violet-400"}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}
        onClick={() => fileRef.current?.click()}
        data-testid="foy-upload-zone"
      >
        <div className="flex flex-col items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-100 to-indigo-100 dark:from-violet-900/30 dark:to-indigo-900/30 flex items-center justify-center">
            <Upload className="w-6 h-6 text-violet-600" />
          </div>
          <div>
            <p className="font-semibold">Föy görselini veya PDF'ini buraya sürükleyin</p>
            <p className="text-xs text-slate-500 mt-1">ya da tıklayarak dosya seçin</p>
          </div>
          <div className="flex gap-2 text-xs text-slate-400">
            <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-[#1A2235]">JPG</span>
            <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-[#1A2235]">PNG</span>
            <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-[#1A2235]">PDF</span>
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => { const f = e.target.files[0]; if (f) onFile(f); }}
          data-testid="foy-file-input"
        />
      </div>
      <p className="text-xs text-slate-400 text-center mt-3 flex items-center justify-center gap-1">
        <Sparkles className="w-3 h-3 text-violet-400" />
        Gemini Vision AI föydeki bilgileri otomatik okur ve doldurur
      </p>
    </div>
  );
}

// ─── İnceleme Adımı ──────────────────────────────────────────────────────────
function ReviewStep({
  preview, suppName, foyNo, setFoyNo, foyDate, setFoyDate,
  currency, setCurrency, editItems, subtotal, vatAmount, total, setTotal,
  note, setNote, onUpdateItem, onBack, onSave,
  supplierMatch, onSwitchSupplier, targetSupplier,
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-sm">
        <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
        <span className="text-emerald-700 dark:text-emerald-300">AI föyü okudu! İstersen düzenle, sonra kaydet.</span>
      </div>

      {/* Görsel */}
      {preview && (
        <div className="rounded-lg overflow-hidden border border-slate-200 dark:border-[#22304A] max-h-48 overflow-y-auto">
          <img src={preview.url} alt="Föy" className="w-full" />
        </div>
      )}

      {/* Meta */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-500">Föy No</label>
          <input className="zen-input mt-1 !text-sm" value={foyNo} onChange={(e) => setFoyNo(e.target.value)} data-testid="foy-no-input" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Tarih</label>
          <input type="date" className="zen-input mt-1 !text-sm" value={foyDate} onChange={(e) => setFoyDate(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Para Birimi</label>
          <select className="zen-input mt-1 !text-sm" value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {["TL", "EUR", "USD"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Tedarikçi</label>
          <div className="mt-1 px-3 py-2 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 text-sm font-semibold text-violet-700 dark:text-violet-300 flex items-center gap-2">
            <span className="flex-1">{suppName}</span>
            <span className="text-[10px] font-normal text-violet-400 bg-violet-100 dark:bg-violet-900/40 px-1.5 py-0.5 rounded">seçili</span>
          </div>
          {/* FAZ 2: MiMo toptancı eşleşmesi — belge farklı firmaya ait görünüyorsa öner */}
          {supplierMatch && supplierMatch.matched && supplierMatch.matched.id !== targetSupplier?.id && (
            <div className="mt-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs">
              <div className="font-semibold text-amber-700 dark:text-amber-300">
                Bu belge <b>{supplierMatch.matched.name}</b> firmasına ait görünüyor
                (eşleşme %{Math.round((supplierMatch.matched.score || 0) * 100)})
              </div>
              <button type="button" onClick={() => onSwitchSupplier(supplierMatch.matched)}
                className="mt-1.5 zen-btn-primary !text-[11px] !py-1" data-testid="foy-switch-supplier">
                {supplierMatch.matched.name} firmasına geç
              </button>
            </div>
          )}
          {supplierMatch && (supplierMatch.candidates || []).length > 1 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(supplierMatch.candidates || []).slice(0, 4).map((cand) => (
                <button key={cand.id} type="button" onClick={() => onSwitchSupplier(cand)}
                  disabled={cand.id === targetSupplier?.id}
                  className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${cand.id === targetSupplier?.id
                    ? "bg-violet-100 text-violet-700 border-violet-300 dark:bg-violet-900/40 dark:text-violet-300 dark:border-violet-700"
                    : "bg-white dark:bg-[#0F172A] border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300 hover:border-violet-400"}`}>
                  {cand.name} {cand.score >= 0.4 ? `%${Math.round(cand.score * 100)}` : ""}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Kalemler */}
      <div>
        <div className="text-xs font-semibold text-slate-500 mb-2">Föy Kalemleri</div>
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {editItems.map((it, idx) => (
            <div key={idx} className="p-3 rounded-lg bg-slate-50 dark:bg-[#0F172A] border border-slate-200 dark:border-[#22304A] space-y-2">
              <input
                className="zen-input !text-xs w-full"
                value={it.description}
                onChange={(e) => onUpdateItem(idx, "description", e.target.value)}
                placeholder="Ürün açıklaması"
              />
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <label className="text-[10px] text-slate-400">Adet</label>
                  <input type="number" className="zen-input !text-xs !py-1 w-full" value={it.qty} onChange={(e) => onUpdateItem(idx, "qty", e.target.value)} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-400">Birim</label>
                  <input className="zen-input !text-xs !py-1 w-full" value={it.unit} onChange={(e) => onUpdateItem(idx, "unit", e.target.value)} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-400">Birim Fiyat</label>
                  <input type="number" className="zen-input !text-xs !py-1 w-full" value={it.unitPrice} onChange={(e) => onUpdateItem(idx, "unitPrice", e.target.value)} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-400">Toplam</label>
                  <input type="number" className="zen-input !text-xs !py-1 w-full" value={it.total} onChange={(e) => onUpdateItem(idx, "total", e.target.value)} />
                </div>
              </div>
            </div>
          ))}
          {editItems.length === 0 && <p className="text-xs text-slate-400 text-center py-3">AI kalem bulamadı. Föy genel toplamı girilecek.</p>}
        </div>
      </div>

      {/* Toplamlar */}
      <div className="p-3 rounded-lg bg-slate-50 dark:bg-[#0F172A] border border-slate-200 dark:border-[#22304A] space-y-1 text-sm">
        <div className="flex justify-between text-slate-500">
          <span>Ara Toplam</span><span className="zen-mono">{subtotal.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {currency}</span>
        </div>
        <div className="flex justify-between text-slate-500">
          <span>KDV</span><span className="zen-mono">{vatAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {currency}</span>
        </div>
        <div className="flex justify-between font-extrabold text-base border-t border-slate-200 dark:border-[#22304A] pt-1">
          <span>Genel Toplam (Borcum)</span>
          <input
            type="number"
            className="zen-input !text-base !font-extrabold !py-0.5 !px-2 w-36 text-right text-rose-500"
            value={total}
            onChange={(e) => setTotal(parseFloat(e.target.value) || 0)}
            data-testid="foy-total-input"
          />
        </div>
      </div>

      {/* Not */}
      <div>
        <label className="text-xs font-semibold text-slate-500">Not (opsiyonel)</label>
        <input className="zen-input mt-1 !text-sm" value={note} onChange={(e) => setNote(e.target.value)} placeholder="İsteğe bağlı not..." />
      </div>

      {/* Butonlar */}
      <div className="flex gap-2 justify-end">
        <button onClick={onBack} className="zen-btn">
          Geri
        </button>
        <button onClick={onSave} className="zen-btn-primary flex items-center gap-2" data-testid="foy-save-btn">
          <Check className="w-4 h-4" />
          Föyü Kaydet ({total.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {currency} borç ekle)
        </button>
      </div>
    </div>
  );
}
